
import React, { useState } from 'react';
import { useAnalyticsData } from '@/hooks/useAnalyticsData';
import TimeRangeSelector from '@/components/charts/TimeRangeSelector';
import GameActivityChart from '@/components/charts/GameActivityChart';
import PerformanceChart from '@/components/charts/PerformanceChart';
import AnalyticsLoading from './AnalyticsLoading';
import NoAnalyticsData from './NoAnalyticsData';

const timeRanges = [
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 14 days', value: '14' },
  { label: 'Last 30 days', value: '30' },
];

const AnalyticsTab = () => {
  const [timeRange, setTimeRange] = useState<string>('7');
  const { 
    loading, 
    hasData, 
    getGameSessionsByDay, 
    getSosEventsByDay, 
    getPerformanceMetrics 
  } = useAnalyticsData(timeRange);
  
  // Convert data for the activity chart
  const getActivityChartData = () => {
    const gameSessionsByDay = getGameSessionsByDay();
    const sosEventsByDay = getSosEventsByDay();
    
    return gameSessionsByDay.map((day, index) => ({
      name: day.displayDay,
      games: day.count,
      sos: sosEventsByDay[index]?.count || 0,
    }));
  };

  // Helper function to safely check if performanceMetrics has entries
  const hasPerformanceMetrics = (): boolean => {
    const metrics = getPerformanceMetrics();
    return metrics instanceof Map && metrics.size > 0;
  };
  
  // Helper function to safely get metric keys
  const getMetricKeys = (): string[] => {
    const metrics = getPerformanceMetrics();
    if (metrics instanceof Map) {
      return Array.from(metrics.keys());
    }
    return [];
  };
  
  // Helper function to safely get metric data
  const getMetricData = (metricName: string): any[] => {
    const metrics = getPerformanceMetrics();
    if (metrics instanceof Map && metrics.has(metricName)) {
      return metrics.get(metricName) || [];
    }
    return [];
  };
  
  // Prepare performance chart components
  const renderPerformanceCharts = () => {
    if (!hasPerformanceMetrics()) return null;
    
    return getMetricKeys().map(metricName => (
      <PerformanceChart
        key={metricName}
        title={metricName === 'fps' ? 'FPS Performance' : 'Memory Usage Performance'}
        description={metricName === 'fps' ? 'Frames per second while playing games' : 'Memory usage in MB while playing games'}
        data={getMetricData(metricName)}
        metricName={metricName}
      />
    ));
  };
  
  if (loading) {
    return <AnalyticsLoading />;
  }
  
  if (!hasData) {
    return <NoAnalyticsData />;
  }
  
  return (
    <div className="space-y-4 mt-4">
      <TimeRangeSelector 
        value={timeRange}
        onChange={setTimeRange}
        ranges={timeRanges}
      />
      
      <GameActivityChart data={getActivityChartData()} />
      
      {renderPerformanceCharts()}
    </div>
  );
};

export default AnalyticsTab;
